import { Navigate, RouteObject } from 'react-router-dom';

import { BroadcastForm } from 'components/broadcasts/broadcast-form';
import { BlankBlock, FeatureGate, RequireUser } from 'components/common';
import {
  PageLayout,
  SinglePane,
  BlankBlockContainer,
} from 'components/layouts';
import { RecognitionForm } from 'components/recognitions/RecognitionForm';
import { TaskForm } from 'components/tasks/TaskForm';
import {
  BroadcastPage,
  LoginPage,
  NoAccessPage,
  PermissionPage,
  RecognitionPage,
  SettingsPage,
  StatisticsPage,
  TaskGroupsView,
  TaskPage,
  TaskManagementPage,
  UserManagementPage,
  UserManagementSubpage,
  UserGroupsManagementSubpage,
  VersionPage,
  HomePage,
} from 'pages';
import {
  ApplicationSettingsPage,
  FunctionalityAdjustmentPage,
  PersonalizationPage,
  BroadcastTagsPage,
} from 'pages/ApplicationSettingsPage';
import { BroadcastView } from 'components/broadcasts/BroadcastView';

const authRoutes: RouteObject = {
  path: '*',
  element: <SinglePane />,
  children: [
    {
      path: 'login',
      element: <LoginPage />,
    },
    {
      path: 'forbidden',
      element: <NoAccessPage />,
    },
  ],
};

const ordinaryRoutes: RouteObject = {
  path: '*',
  element: <PageLayout />,
  children: [
    {
      index: true,
      element: <RequireUser allowedRoles={['SUPER_ADMIN', 'ADMIN']} />,
    },
    {
      path: 'home',
      element: <RequireUser allowedRoles={['SUPER_ADMIN', 'ADMIN']} />,
      children: [
        {
          index: true,
          loader: () => import('pages/HomePage'),
          element: <HomePage />,
        },
      ],
    },
    {
      path: 'tasks',
      element: (
        <FeatureGate routeMode feature="taskManagement">
          <RequireUser allowedRoles={['SUPER_ADMIN', 'ADMIN']} />
        </FeatureGate>
      ),
      children: [
        {
          element: <TaskManagementPage />,
          children: [
            {
              index: true,
              loader: () => import('pages/TaskPage'),
              element: <TaskPage />,
            },
            {
              element: <TaskPage />,
              children: [
                {
                  path: 'create',
                  element: <TaskForm />,
                },
                {
                  path: ':taskId',
                  element: <TaskForm />,
                },
                {
                  path: 'task-not-found',
                  element: <TaskForm />,
                },
              ],
            },
            {
              path: 'recognitions',
              element: (
                <FeatureGate routeMode feature="recognition">
                  <RecognitionPage />
                </FeatureGate>
              ),
              children: [
                {
                  path: 'create',
                  element: <RecognitionForm />,
                },
                {
                  path: ':recognitionId',
                  element: <RecognitionForm />,
                },
                {
                  path: 'recognition-not-found',
                  element: <RecognitionForm />,
                },
              ],
            },
            {
              path: 'taskGroups',
              element: (
                <FeatureGate routeMode feature="taskGroups">
                  <TaskGroupsView />
                </FeatureGate>
              ),
            },
          ],
        },
      ],
    },
    {
      path: 'broadcasts',
      element: (
        <FeatureGate routeMode feature="broadcasts">
          <RequireUser allowedRoles={['SUPER_ADMIN', 'ADMIN']} />
        </FeatureGate>
      ),
      children: [
        {
          index: true,
          loader: () => import('pages/BroadcastPage'),
          element: <BroadcastPage />,
        },
        {
          element: <BroadcastPage />,
          children: [
            {
              path: 'create',
              element: <BroadcastView />,
            },
            {
              path: ':broadcastId',
              element: <BroadcastView />,
            },
            {
              path: 'broadcast-not-found',
              element: <BroadcastForm />,
            },
          ],
        },
      ],
    },
    {
      path: 'statistics',
      element: (
        <FeatureGate routeMode feature="statistics">
          <RequireUser allowedRoles={['SUPER_ADMIN', 'ADMIN']} />
        </FeatureGate>
      ),
      children: [
        {
          index: true,
          loader: () => import('pages/StatisticsPage'),
          element: <StatisticsPage />,
        },
      ],
    },
    {
      path: 'settings',
      element: (
        <FeatureGate routeMode feature="settings">
          <RequireUser allowedRoles={['SUPER_ADMIN', 'ADMIN']} />
        </FeatureGate>
      ),
      children: [
        {
          index: true,
          loader: () => import('pages/SettingsPage'),
          element: <Navigate replace to="admins" />,
        },
        {
          element: <SettingsPage />,
          children: [
            {
              path: 'admins',
              element: (
                <FeatureGate routeMode feature="adminManagement">
                  <RequireUser allowedRoles={['SUPER_ADMIN']} />
                </FeatureGate>
              ),
              children: [
                {
                  index: true,
                  element: <PermissionPage />,
                },
              ],
            },
            {
              path: 'users',
              element: (
                <FeatureGate routeMode feature="userManagement">
                  <UserManagementPage />
                </FeatureGate>
              ),
              children: [
                {
                  index: true,
                  element: <UserManagementSubpage />,
                },
                {
                  path: 'groups',
                  element: (
                    <FeatureGate routeMode feature="userGroupsRoles">
                      <UserGroupsManagementSubpage />
                    </FeatureGate>
                  ),
                },
              ],
            },
            {
              path: 'application',
              element: (
                <FeatureGate routeMode feature="applicationSettings">
                  <ApplicationSettingsPage />
                </FeatureGate>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate replace to="functionality-adjustment" />,
                },
                {
                  path: 'functionality-adjustment',
                  element: <FunctionalityAdjustmentPage />,
                },
                {
                  path: 'personalization',
                  element: <PersonalizationPage />,
                },
                {
                  path: 'broadcast-tags',
                  element: <BroadcastTagsPage />,
                },
              ],
            },
            {
              path: 'versions',
              element: (
                <FeatureGate routeMode feature="versions">
                  <VersionPage />
                </FeatureGate>
              ),
            },
          ],
        },
      ],
    },
    {
      path: 'not-found',
      element: (
        <BlankBlockContainer>
          <BlankBlock
            header="Sorry, we couldn't find this page."
            message="Double check the URL and try again."
          />
        </BlankBlockContainer>
      ),
    },
    {
      path: '*',
      element: <RequireUser allowedRoles={['SUPER_ADMIN', 'ADMIN']} />,
    },
  ],
};

const routes: RouteObject[] = [authRoutes, ordinaryRoutes];

export default routes;
